import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import './CreateAuction.css';
import MasterService from '../../services/MasterService';

const EditAuction = () => {
    const navigate = useNavigate();
    const { auctionId } = useParams();
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(!!auctionId);
    const [cities, setCities] = useState([]);
    const [regions, setRegions] = useState([]);
    const [buyers, setBuyers] = useState([]);
    const [selectedBuyers, setSelectedBuyers] = useState([]);
    const [buyerVehicles, setBuyerVehicles] = useState([]);
    const [selectAllBuyers, setSelectAllBuyers] = useState(false);
    const [selectAllVehicles, setSelectAllVehicles] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        auctionId: '',
        auctionName: '',
        cityId: '',
        regionId: '',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        selectedBuyers: [],
        selectedVehicles: []
    });

    // Fetch cities and regions
    useEffect(() => {
        fetchCities();
    }, []);

    // Fetch auction details if in edit mode
    useEffect(() => {
        if (isEditMode && auctionId) {
            fetchAuctionDetails();
        }
    }, [isEditMode, auctionId]);

    // Fetch buyers when city is prefilled in edit mode
    useEffect(() => {
        if (isEditMode && formData.cityId) {
            fetchBuyers(formData.cityId);
        }
    }, [isEditMode, formData.cityId]);

    const fetchAuctionDetails = async () => {
        setLoading(true);
        try {
            const response = await MasterService.getAuctionDetails(auctionId);
            if (response.data && response.data.data) {
                const auctionData = response.data.data;
                
                // Update form state with auction details
                setFormData({
                    auctionId: auctionData.auction_id,
                    auctionName: auctionData.auction_name,
                    cityId: auctionData.city.toString(),
                    regionId: auctionData.region.toString(),
                    startDate: auctionData.start_date,
                    endDate: auctionData.end_date,
                    startTime: auctionData.start_time ? auctionData.start_time.substring(0, 5) : '',
                    endTime: auctionData.end_time ? auctionData.end_time.substring(0, 5) : '',
                    selectedBuyers: auctionData.selected_dealers || [],
                    selectedVehicles: auctionData.selected_leads ? auctionData.selected_leads.map(lead => lead.lead_id) : []
                });

                // Update selected buyers state
                setSelectedBuyers(auctionData.selected_dealers || []);
                
                // Check if all buyers are selected
                if (buyers.length > 0) {
                    const allBuyerIds = buyers.map(buyer => parseInt(buyer.id));
                    const allSelected = allBuyerIds.every(id => auctionData.selected_dealers.includes(id));
                    setSelectAllBuyers(allSelected);
                }

                // Fetch vehicles for selected buyers
                if (auctionData.selected_dealers && auctionData.selected_dealers.length > 0) {
                    const response = await MasterService.getLeadsByDealers(auctionData.selected_dealers);
                    if (response.data && response.data.data) {
                        const vehicles = response.data.data.leads || [];
                        setBuyerVehicles(vehicles);
                        
                        // Check if all vehicles are selected
                        if(vehicles?.length) {
                            const allVehicleIds = vehicles.map(vehicle => vehicle.lead_id) || [];
                            const allSelected = allVehicleIds.every(id => 
                                auctionData.selected_leads.some(lead => lead.lead_id === id)
                            );
                            setSelectAllVehicles(allSelected);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching auction details:', error);
            toast.error('Failed to fetch auction details');
        } finally {
            setLoading(false);
        }
    };

    // Handle select all buyers
    const handleSelectAllBuyers = async (checked) => {
        setSelectAllBuyers(checked);
        if (checked) {
            // Select all buyers
            const allBuyerIds = buyers.map(buyer => parseInt(buyer.id));
            setSelectedBuyers(allBuyerIds);
            setFormData(prev => ({
                ...prev,
                selectedBuyers: allBuyerIds
            }));
        } else {
            // Deselect all buyers
            setSelectedBuyers([]);
            setFormData(prev => ({
                ...prev,
                selectedBuyers: []
            }));
        }

        // If all buyers are selected, call getLeadsByDealers with all buyer IDs
        if (checked && buyers.length > 0) {
            console.log('All buyers selected, fetching vehicles for all dealers');
            const allBuyerIds = buyers.map(buyer => parseInt(buyer.id));
            const response = await MasterService.getLeadsByDealers(allBuyerIds);
            if (response.data && response.data.data) {
                const vehicles = response.data.data.leads || [];
                setBuyerVehicles(vehicles);
            }
        } else {
            const response = await MasterService.getLeadsByDealers([]);
            if (response.data && response.data.data) {
                const vehicles = response.data.data.leads || [];
                setBuyerVehicles(vehicles);
            }
        }
    };

    // Handle select all vehicles
    const handleSelectAllVehicles = (checked) => {
        setSelectAllVehicles(checked);
        if (checked) {
            // Select all vehicles
            const allVehicleIds = buyerVehicles.map(vehicle => parseInt(vehicle.lead_id));
            setFormData(prev => ({
                ...prev,
                selectedVehicles: allVehicleIds
            }));
        } else {
            // Deselect all vehicles
            setFormData(prev => ({
                ...prev,
                selectedVehicles: []
            }));
        }
    };

    const fetchCities = async () => {
        try {
            const response = await MasterService.getCities();
            console.log("**************",response)
            if (response.data && response.data.data) {
                setCities(response.data.data.city || []);
                // Set regions/states from response
                setRegions(response.data.data.state || []);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            toast.error('Failed to fetch cities');
        }
    };

    const fetchBuyers = async (cityId = null) => {
        try {
            let buyersData = [];
            
            if (cityId) {
                // Fetch dealers by city
                const response = await MasterService.getDealersByCity([parseInt(cityId)]);
                if (response.data && response.data.data) {
                    buyersData = response.data.data.map(dealer => ({
                        id: dealer.id,
                        organization: dealer.organization,
                        gcd_code: dealer.gcd_code || `GCD${dealer.id}`
                    }));
                }
            }
            
            setBuyers(buyersData);
        } catch (error) {
            console.error('Error fetching buyers:', error);
            toast.error('Failed to fetch buyers');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // If region changes, clear city selection
        if (name === 'regionId') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                cityId: '' // Clear city when region changes
            }));
            // Clear buyers when region changes
            setBuyers([]);
        } else if (name === 'cityId') {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
            // Clear selected buyers when city changes
            setSelectedBuyers([]);
            setBuyerVehicles([]);
            // Fetch buyers when city is selected
            if (value) {
                fetchBuyers(value);
            } else {
                // Clear buyers when city is cleared
                setBuyers([]);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleBuyerSelection = async (buyerId, isChecked) => {
        try {
            console.log("***************", buyerId, isChecked);
            console.log("Current selectedBuyers before:", selectedBuyers);
            
            // Ensure buyerId is a number for consistent comparison
            const numericBuyerId = parseInt(buyerId);
            console.log("Numeric buyer ID:", numericBuyerId);
            
            let updatedSelectedBuyers;
            
            if (isChecked) {
                // Add buyer to selection (avoid duplicates)
                if (!selectedBuyers.includes(numericBuyerId)) {
                    updatedSelectedBuyers = [...selectedBuyers, numericBuyerId];
                    console.log("Adding buyer to selection");
                } else {
                    updatedSelectedBuyers = selectedBuyers; // No change if already selected
                    console.log("Buyer already selected, no change");
                }
            } else {
                // Remove buyer from selection
                updatedSelectedBuyers = selectedBuyers.filter(id => id !== numericBuyerId);
                console.log("Removing buyer from selection");
            }
            
            console.log("Updated selectedBuyers:", updatedSelectedBuyers);
            
            // Update both selectedBuyers state and formData.selectedBuyers
            setSelectedBuyers(updatedSelectedBuyers);
            setFormData(prev => ({
                ...prev,
                selectedBuyers: updatedSelectedBuyers
            }));
            
            // Add a small delay to ensure state is updated
            setTimeout(() => {
                console.log("State after update:", selectedBuyers);
            }, 100);
            
            // Fetch vehicles for selected buyers
            if (updatedSelectedBuyers.length > 0) {
                console.log('Fetching vehicles for dealer IDs:', updatedSelectedBuyers);
                const response = await MasterService.getLeadsByDealers(updatedSelectedBuyers);
                if (response.data && response.data.data) {
                    const vehicles = response.data.data.leads || [];
                    setBuyerVehicles(vehicles);
                }
            } else {
                setBuyerVehicles([]);
                // Clear selected vehicles when no buyers selected
                setFormData(prev => ({
                    ...prev,
                    selectedVehicles: []
                }));
            }
        } catch (error) {
            console.error('Error fetching buyer vehicles:', error);
            toast.error('Failed to fetch vehicles');
        }
    };

    const handleVehicleSelection = (vehicleId, isChecked) => {
        const numericVehicleId = parseInt(vehicleId);
        let updatedSelectedVehicles;
        
        if (isChecked) {
            // Add vehicle to selection (avoid duplicates)
            if (!formData.selectedVehicles.includes(numericVehicleId)) {
                updatedSelectedVehicles = [...formData.selectedVehicles, numericVehicleId];
            } else {
                updatedSelectedVehicles = formData.selectedVehicles; // No change if already selected
            }
        } else {
            // Remove vehicle from selection
            updatedSelectedVehicles = formData.selectedVehicles.filter(id => id !== numericVehicleId);
        }
        
        setFormData(prev => ({
            ...prev,
            selectedVehicles: updatedSelectedVehicles
        }));
    };

    const getMinStartDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const getMinEndDate = () => {
        if (formData.startDate) {
            return formData.startDate;
        }
        return getMinStartDate();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            // Validate form
            if (!formData.auctionName || !formData.cityId || !formData.regionId || 
                !formData.startDate || !formData.endDate || !formData.startTime || !formData.endTime) {
                toast.error('Please fill all required fields');
                return;
            }

            if (formData.selectedBuyers.length === 0) {
                toast.error('Please select at least one buyer');
                return;
            }

            if (formData.selectedVehicles.length === 0) {
                toast.error('Please select at least one vehicle');
                return;
            }

            // Prepare auction data
            const auctionData = {
                ...formData,
                selectedBuyers: selectedBuyers,
                selectedVehicles: formData.selectedVehicles.map(vehicleId => 
                    buyerVehicles.find(v => v.lead_id === vehicleId)
                ).filter(v => v), // Filter out any undefined vehicles
                isEditMode: isEditMode,
                auctionId: isEditMode ? parseInt(auctionId) : null
            };
            
            console.log('Updating auction:', auctionData);
            
            // Navigate to auction details page with the data
            navigate('/auction/details', { state: { auctionData } });
            
        } catch (error) {
            console.error('Error updating auction:', error);
            toast.error('Failed to update auction');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/auction-list');
    };

    if (loading && isEditMode) {
        return (
            <div className="create-auction-container">
                <div className="auction-loading-container">
                    <div className="auction-loading-spinner">Loading auction details...</div>
                </div>
            </div>
        );
    }
    console.log("****************",formData)
    return (
        <div className="create-auction-container">
            <div className="create-auction-header">
                <div className="header-content">
                  
                     <div className="arrow-bck"   onClick={handleBack}>
                   <i className='ic-arrow_back'></i>
                </div>
                    <h1>{isEditMode ? 'Edit Auction' : 'Create Auction'}</h1>
                </div>
            </div>

            <div className="create-auction-content">
                <form onSubmit={handleSubmit} className="auction-form">
                    <div className="form-section">
                        <h2>Auction Details</h2>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="auctionName">Auction Name *</label>
                                <input
                                    type="text"
                                    id="auctionName"
                                    name="auctionName"
                                    value={formData.auctionName}
                                    onChange={handleInputChange}
                                    placeholder="Enter auction name"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="regionId">Region *</label>
                                <Select
                                    id="regionId"
                                    name="regionId"
                                    value={formData.regionId ? { value: formData.regionId, label: regions.find(r => r.id.toString() === formData.regionId)?.name || formData.regionId } : null}
                                    onChange={(selectedOption) => handleInputChange({ target: { name: 'regionId', value: selectedOption ? selectedOption.value : '' } })}
                                    options={[
                                        { value: '', label: 'Select Region' },
                                        ...regions.map(region => ({ value: region.id.toString(), label: region.name }))
                                    ]}
                                    placeholder="Select Region"
                                    isClearable
                                    isSearchable
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="cityId">City *</label>
                                <Select
                                    id="cityId"
                                    name="cityId"
                                    value={formData.cityId ? { value: formData.cityId, label: cities.find(c => c.id.toString() === formData.cityId)?.name || formData.cityId } : null}
                                    onChange={(selectedOption) => handleInputChange({ target: { name: 'cityId', value: selectedOption ? selectedOption.value : '' } })}
                                    options={[
                                        { value: '', label: 'Select City' },
                                        ...cities
                                            .filter(city => formData.regionId && city.state_id === parseInt(formData.regionId))
                                            .map(city => ({ value: city.id.toString(), label: city.name }))
                                    ]}
                                    placeholder="Select City"
                                    isClearable
                                    isSearchable
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="startDate">Start Date *</label>
                                <DatePicker
                                    id="startDate"
                                    selected={formData.startDate ? new Date(formData.startDate) : null}
                                    onChange={(date) => {
                                        const formattedDate = date ? date.toLocaleDateString('en-CA') : '';
                                        handleInputChange({ target: { name: 'startDate', value: formattedDate } });
                                    }}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="Select Start Date"
                                    minDate={new Date(getMinStartDate())}
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="startTime">Start Time *</label>
                                <DatePicker
                                    id="startTime"
                                    value={formData.startTime}
                                    onChange={(date) => {
                                        const timeString = date ? date.toTimeString().slice(0, 5) : '';
                                        handleInputChange({ target: { name: 'startTime', value: timeString } });
                                    }}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    placeholderText="Select Start Time"
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="endDate">End Date *</label>
                                <DatePicker
                                    id="endDate"
                                    selected={formData.endDate ? new Date(formData.endDate) : null}
                                    onChange={(date) => {
                                        const formattedDate = date ? date.toLocaleDateString('en-CA') : '';
                                        handleInputChange({ target: { name: 'endDate', value: formattedDate } });
                                    }}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="Select End Date"
                                    minDate={new Date(getMinEndDate())}
                                    className="form-control"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="endTime">End Time *</label>
                                <DatePicker
                                    id="endTime"
                                    value={formData.endTime}
                                    onChange={(date) => {
                                        const timeString = date ? date.toTimeString().slice(0, 5) : '';
                                        handleInputChange({ target: { name: 'endTime', value: timeString } });
                                    }}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    placeholderText="Select End Time"
                                    className="form-control"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h2>Manage Buyers</h2>
                        <div className="form-group">
                            <label>Select Buyers</label>
                            <div className='select-buyer-outer'>
                            <div className="select-all-container">
                                <div className="custom-control custom-checkbox">
                                    <input
                                        type="checkbox"
                                        id="select-all-buyers"
                                        className="custom-control-input"
                                        checked={selectAllBuyers}
                                        onChange={(e) => handleSelectAllBuyers(e.target.checked)}
                                    />
                                    <label className="custom-control-label" htmlFor="select-all-buyers">
                                        Select All Buyers
                                    </label>
                                </div>
                            </div>
                            <div className="multi-select-container">
                                {buyers.map(buyer => {
                                    const buyerIdNum = parseInt(buyer.id);
                                    const isSelected = selectedBuyers.includes(buyerIdNum);
                                    console.log(`Buyer ${buyer.id} (${buyerIdNum}) selected:`, isSelected, 'selectedBuyers:', selectedBuyers);
                                    return (
                                    <div key={buyer.id} className="custom-control custom-checkbox">
                                        <input
                                            id={`buyer-${buyer.id}`}
                                            type="checkbox"
                                            className="custom-control-input"
                                            value={buyer.id}
                                            onChange={(e) => handleBuyerSelection(buyer.id, e.target.checked)}
                                            checked={isSelected}
                                            name={`buyer-${buyer.id}`}
                                        />
                                        <label className="custom-control-label" htmlFor={`buyer-${buyer.id}`}>
                                            {buyer.organization} ({buyer.gcd_code})
                                        </label>
                                    </div>
                                    );
                                })}
                            </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h2>Add Vehicle</h2>
                        <div className="form-group">
                            <label>Select Vehicles</label>
                            <div className='select-buyer-outer'>
                            <div className="select-all-container">
                                <div className="custom-control custom-checkbox">
                                    <input
                                        type="checkbox"
                                        id="select-all-vehicles"
                                        className="custom-control-input"
                                        checked={selectAllVehicles}
                                        onChange={(e) => handleSelectAllVehicles(e.target.checked)}
                                    />
                                    <label className="custom-control-label" htmlFor="select-all-vehicles">
                                        Select All Vehicles
                                    </label>
                                </div>
                            </div>
                            <div className="multi-select-container">
                                {buyerVehicles.length > 0 ? (
                                    buyerVehicles.map(vehicle => (
                                        <div key={vehicle.lead_id} className="custom-control custom-checkbox">
                                            <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                id={`vehicle-${vehicle.lead_id}`}
                                                value={vehicle.lead_id}
                                                checked={formData.selectedVehicles.includes(vehicle.lead_id)}
                                                onChange={(e) => handleVehicleSelection(vehicle.lead_id, e.target.checked)}
                                            />
                                            <label className="custom-control-label" htmlFor={`vehicle-${vehicle.lead_id}`}>
                                                {vehicle.make_name} {vehicle.model_name} ({vehicle.plate_number})
                                            </label>
                                        </div>
                                    ))
                                ) : (
                                    <p className="no-vehicles-message">
                                        {selectedBuyers.length > 0 ? 'No vehicles found for selected buyers' : 'Please select buyers first to see available vehicles'}
                                    </p>
                                )}
                            </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="button" className="btn-line" onClick={handleBack}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? 'Saving...' : (isEditMode ? 'Update Auction' : 'Create Auction')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditAuction;
